import { useAppDispatch, useAppSelector } from '@redux/store';
import { decomissionUnsetParameter, getParameterAvailables } from '@services/DataBase/Parameter.service';
import { DataGrid, generateBasicColumn, Grid, Modal, Select, Error, GridContainerFullHeight, IAction } from '@ts/storybook';
import { CEntity, COrganization, IEntity, IForm, CParameter, CRole, CUser } from '@ts/ui-models';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

export type TTypeParameter = 'app' | 'organization' | 'role' | 'user';
interface DataGridParameterSettingsProps extends IForm {
  type: TTypeParameter;
  listParameters?: CParameter[];
  //values = COrganization or CRole or CUser
}

const DataGridParameterSettings = (props: DataGridParameterSettingsProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const badge = useAppSelector(state => state.global.sessionBadge);
  const { mode, type = 'organization', values, listParameters } = props;
  const isViewMode = mode === 'view';
  const xTypeId = values?.getID() ?? null; // organizationId or roleId or userId, null=app
  const [listSettingsParameter, setListSettingsParameter] = useState<CParameter[] | null>(null);
  const [state, setState] = useState<{
    showModal: boolean;
    showDeleteModal: boolean;
    parameterToRemove: CParameter | null;
  }>({
    showModal: false,
    showDeleteModal: false,
    parameterToRemove: null,
  });
  const [listAvailableParameters, setListavailableParameters] = useState<CEntity<IEntity>[]>([]);

  //#region Handlers
  const onClickAddParameter = () => {
    setState({ ...state, showModal: true });
  };

  const onChangeParameter = value => {
    if (value === null) {
      return;
    }
    const parameterType = listAvailableParameters.find(p => p.logicalId === value);
    const parameter = {
      xTypeId: xTypeId,
      name: parameterType?.getID().split('_')[1],
      parameterTypeId: parameterType?.getID(),
      parameterType,
      value: {},
      currentOrganizationId: badge?.organizationId,
    };

    return navigate('/parameter/new', { state: { title: parameterType?.getName(), entity: parameter } });
  };

  const onDeleteParameter = async () => {
    if (!badge || !listSettingsParameter) return;
    await decomissionUnsetParameter(dispatch, type, xTypeId, badge, state.parameterToRemove?.getID() ?? '');
    setListSettingsParameter(listSettingsParameter.filter(p => p.id !== state.parameterToRemove?.getID()));
    setState({ ...state, showDeleteModal: false });
  };
  //#endregion

  const extraAction = (): IAction[] => {
    const actions = [
      {
        id: 'copyParameter',
        label: 'Copy',
        icon: 'content_copy',
        onClick: handleClick => {
          const parameter = handleClick();
          const jsonString = JSON.stringify(parameter.value, null, 2);
          navigator.clipboard
            .writeText(jsonString)
            .then(() => {
              // alert('JSON copiado al portapapeles');
            })
            .catch(err => {
              console.error('Error al copiar JSON:', err);
            });
        },
      },
      {
        id: 'editParameter',
        label: 'Edit',
        icon: 'editor-mode-edit',
        onClick: handleClick => {
          navigate(`/parameter/${handleClick().id}/edit`);
        },
      },
      {
        id: 'editParameter2',
        label: 'Edit in JSON',
        icon: 'data_object',
        onClick: handleClick => {
          navigate(`/parameter/${handleClick().id}/edit`, { state: { openInJSON: true } });
        },
      },
      {
        id: 'deleteParameter',
        label: 'Delete',
        icon: 'action-delete',
        onClick: handleClick => {
          setState({ ...state, showDeleteModal: true, parameterToRemove: new CParameter(handleClick()) });
        },
      },
    ];

    return !isViewMode ? actions : actions.filter(a => a.id === 'copyParameter');
  };

  //#region Render

  const renderColumns = () => {
    return [
      generateBasicColumn({ field: 'name' }, { field: 'parameterType.name' }),
      generateBasicColumn(
        {
          field: 'value',
          dataFormat: v => {
            return v.toString();
          },
        },
        { field: 'value' }
      ),
    ];
  };

  const renderModalSelectParameter = () => {
    return (
      <Modal
        title={'Select the parameter type'}
        withHeader
        open={state.showModal}
        onClose={() => setState({ ...state, showModal: false })}
      >
        <Grid container p={2} mt={2} className="w-[500px]">
          <Select
            label="Select type..."
            style={{ minWidth: '300px' }}
            value={undefined}
            onChange={onChangeParameter}
            options={listAvailableParameters?.map(p => ({ value: p.getID(), label: p.getName() }))}
          />
        </Grid>
      </Modal>
    );
  };

  const renderModalDelete = () => {
    return (
      <Modal
        withHeader
        open={state.showDeleteModal}
        title={['You are about to delete ', state.parameterToRemove?.parameterType.name, '. ', 'Are you sure?']}
        labelOk="Delete"
        labelCancel="Cancel"
        onSubmit={() => onDeleteParameter()}
        onCancel={() => setState({ ...state, showDeleteModal: false, parameterToRemove: null })}
        fullWidth
        maxWidth="sm"
        variant="error"
      />
    );
  };
  //#endregion

  useEffect(() => {
    const loadParameters = async () => {
      if (!badge || !listSettingsParameter) return;
      const p = await getParameterAvailables(dispatch, type, xTypeId, badge);
      if (!p) return;
      setListavailableParameters(p);
    };
    loadParameters();
  }, [listSettingsParameter]);

  useEffect(() => {
    switch (type) {
      case 'app':
        if (listParameters) setListSettingsParameter(listParameters);
        break;
      case 'organization':
        const mapListParametersOrganization = ((values as COrganization)?.xOrganizationParameters ?? []).map(p => {
          return new CParameter(p.parameter);
        });
        setListSettingsParameter(mapListParametersOrganization);
        break;
      case 'role':
        const mapListParametersRole = ((values as CRole)?.xRoleParameters ?? []).map(p => {
          return new CParameter(p.parameter);
        });
        setListSettingsParameter(mapListParametersRole);
        break;
      case 'user':
        const mapListxUserProfileParameters = ((values as CUser)?.xUserProfileParameters ?? []).map(p => {
          return new CParameter(p.parameter);
        });
        setListSettingsParameter(mapListxUserProfileParameters);
        break;
      default:
        break;
    }
  }, [values, listParameters]);

  if (type === 'organization' && badge?.organizationId !== values.getID())
    return (
      <GridContainerFullHeight>
        <Error message="First, you must select a role from that organisation." />;
      </GridContainerFullHeight>
    );
  return (
    <Grid container>
      <DataGrid
        key={`dataGrid-organization-setting`}
        componentSettings={{
          sortableColumns: [
            {
              field: 'name',
            },
          ],
          extraColumns: [],
        }}
        configComponent={{
          aclId: 'dataGridOrgSetting',
          componentId: 'dataGridOrgSetting',
        }}
        dataGridConfig={{ sortModel: [{ field: 'name', sort: 'asc' }], widthColumns: { name: 250, value: 800 } }}
        propsDataGrid={{
          dataGridMode: 'client',
          withoutFooter: true,
          enabledFilters: true,
          rowHeight: 60,
        }}
        propsDataGridToolbar={{
          withoutSave: true,
          withoutTotal: true,
          withoutFilters: true,
          onClickCreateEntity: isViewMode ? undefined : onClickAddParameter,
        }}
        propsDataGridQuickAccess={{
          extraActions: extraAction(),
          withoutDefaultActions: true,
          withoutMoreActions: true,
        }}
        columns={renderColumns()}
        rows={{ count: listSettingsParameter?.length ?? 0, rows: listSettingsParameter ?? [] }}
      />
      {renderModalSelectParameter()}
      {renderModalDelete()}
    </Grid>
  );
};

export default DataGridParameterSettings;
